/**
 * Evidence Locker storage add-on: GB-based tiers independent of demand volume.
 * Active demands, archive, and seats are unlimited and not billed here.
 */

import { matterDemandId } from './demandId.js';
import { debtorCompanyPrimaryName } from './debtorIdentity.js';
import {
  evidenceViewerUrl,
  isOpenableAttachmentUrl,
  resolveUploadBlobUrl,
} from './uploadBlobStore.js';

export const EVIDENCE_VAULT_CURRENCY = 'USD';
export const BYTES_PER_GB = 1024 * 1024 * 1024;

/** Soft-warn threshold before hard block on uploads. */
export const EVIDENCE_VAULT_WARN_RATIO = 0.8;

/**
 * Included 2 GB with every account; paid Locker add-ons increase the cap.
 * Uploads stop at the plan cap until files are deleted or the plan is larger.
 */
export const EVIDENCE_VAULT_TIERS = [
  {
    id: 'included',
    label: 'Included',
    gbLimit: 2,
    monthlyPrice: 0,
    stripePriceId: null,
    description: '2 GB evidence storage included with every account.',
  },
  {
    id: 'starter',
    label: 'Locker 25',
    gbLimit: 25,
    monthlyPrice: 15,
    stripePriceId: 'price_demandjet_vault_starter',
    description: '25 GB for solo and small firms.',
  },
  {
    id: 'growth',
    label: 'Locker 100',
    gbLimit: 100,
    monthlyPrice: 39,
    stripePriceId: 'price_demandjet_vault_growth',
    description: '100 GB for growing caseloads.',
  },
  {
    id: 'scale',
    label: 'Locker 500',
    gbLimit: 500,
    monthlyPrice: 99,
    stripePriceId: 'price_demandjet_vault_scale',
    description: '500 GB for high-volume collections.',
  },
];

export function getEvidenceVaultTiers() {
  return EVIDENCE_VAULT_TIERS.map((tier) => ({ ...tier }));
}

export function getEvidenceVaultTiersById(tierId) {
  const id = String(tierId || 'included').toLowerCase();
  return EVIDENCE_VAULT_TIERS.find((tier) => tier.id === id) || EVIDENCE_VAULT_TIERS[0];
}

export function getEvidenceVaultTiersAbove(tierId) {
  const tiers = getEvidenceVaultTiers();
  const index = tiers.findIndex((tier) => tier.id === String(tierId || 'included'));
  if (index < 0) return tiers.filter((tier) => tier.id !== 'included');
  return tiers.slice(index + 1);
}

export function resolveEvidenceVaultPlanId(billing = {}) {
  const explicit = String(billing?.evidenceVaultPlanId || '').toLowerCase();
  if (EVIDENCE_VAULT_TIERS.some((tier) => tier.id === explicit)) return explicit;
  return 'included';
}

export function getEvidenceVaultEntitlement(billing = {}) {
  const planId = resolveEvidenceVaultPlanId(billing);
  const tier = getEvidenceVaultTiersById(planId);
  const paid = tier.id !== 'included';
  const limitBytes = Math.round(tier.gbLimit * BYTES_PER_GB);
  return {
    planId: tier.id,
    tierId: tier.id,
    tierLabel: tier.label,
    monthlyPrice: tier.monthlyPrice,
    currency: EVIDENCE_VAULT_CURRENCY,
    gbLimit: tier.gbLimit,
    limitBytes,
    stripePriceId: tier.stripePriceId || null,
    stripeSubscriptionItemId: billing?.evidenceVaultStripeItemId || null,
    description: tier.description,
    enabled: paid,
  };
}

/** Deterministic demo size estimate when real byte length is unknown. */
export function estimateFileBytes(name, knownSize) {
  const known = Number(knownSize);
  if (Number.isFinite(known) && known > 0) return Math.floor(known);
  const s = String(name || 'file');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  }
  // ~0.4–3.5 MB per vault file for demo metering
  return 400_000 + (hash % 3_100_000);
}

function basenameFromUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return [];
  if (raw.startsWith('pending-upload://')) {
    return raw
      .slice('pending-upload://'.length)
      .split(',')
      .map((part) => {
        try {
          return decodeURIComponent(part.trim());
        } catch {
          return part.trim();
        }
      })
      .filter(Boolean);
  }
  if (raw.startsWith('catalyst://')) {
    const parts = raw.split('/');
    return [parts[parts.length - 1] || 'vault-file'].filter(Boolean);
  }
  try {
    const path = raw.includes('://') ? new URL(raw).pathname : raw;
    const parts = path.split('/');
    return [decodeURIComponent(parts[parts.length - 1] || '')].filter(Boolean);
  } catch {
    const parts = raw.split('/');
    return [parts[parts.length - 1] || ''].filter(Boolean);
  }
}

function attachmentName(att) {
  return String(att?.name || att?.fileName || att?.filename || att?.id || '').trim();
}

const RETAINED_EVIDENCE_KEY = 'demandjet.retainedEvidenceFiles';

function matterIsDeleted(matter) {
  const flag = matter?.Deleted;
  return flag === true || flag === 1 || flag === '1';
}

/**
 * Files kept after an archived demand is deleted.
 * Evidence Locker is paid storage, so those files stay when the demand is removed.
 */
export function loadRetainedEvidenceFiles() {
  try {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(RETAINED_EVIDENCE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveRetainedEvidenceFiles(files = []) {
  const next = Array.isArray(files) ? files : [];
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(RETAINED_EVIDENCE_KEY, JSON.stringify(next));
    }
  } catch {
    // Private mode or a full quota should not block deleting the demand.
  }
  return next;
}

function filesFromMatters(matters = []) {
  const files = [];
  const seen = new Set();

  for (const matter of matters || []) {
    const matterId = String(matter?.ROWID || '');
    if (!matterId) continue;
    const debtorName =
      debtorCompanyPrimaryName(matter) ||
      String(matter.External_Invoice_ID || '').trim() ||
      matterId;
    const demandId = matterDemandId(matter) || '';
    const demandDeleted = matterIsDeleted(matter);

    for (const att of matter.Attachments || []) {
      const name = attachmentName(att);
      if (!name) continue;
      const key = `${matterId}::att::${name.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      files.push({
        id: key,
        name,
        kind: att.kind || att.type || 'Attachment',
        source: 'attachment',
        matterId,
        debtorName,
        matterLabel: debtorName,
        demandId,
        demandDeleted,
        bytes: estimateFileBytes(name, att.size || att.bytes),
        url: att.url || att.href || null,
      });
    }

    for (const [field, kind] of [
      ['Evidence_Vault_URL', 'Evidence'],
      ['Document_Vault_URL', 'Demand document'],
    ]) {
      const names = basenameFromUrl(matter[field]);
      for (const name of names) {
        const key = `${matterId}::${field}::${name.toLowerCase()}`;
        if (seen.has(key)) continue;
        seen.add(key);
        files.push({
          id: key,
          name,
          kind,
          source: field,
          matterId,
          debtorName,
          matterLabel: debtorName,
          demandId,
          demandDeleted,
          bytes: estimateFileBytes(name),
          url: String(matter[field] || '') || null,
        });
      }
    }
  }

  return files;
}

/** Remember locker files for demands that are about to leave the demand lists. */
export function retainEvidenceForMatters(matters = []) {
  const incoming = filesFromMatters(matters).map((file) => ({
    ...file,
    demandDeleted: true,
  }));
  const matterIds = new Set(incoming.map((file) => String(file.matterId)));
  const kept = loadRetainedEvidenceFiles().filter(
    (file) => !matterIds.has(String(file?.matterId || ''))
  );
  return saveRetainedEvidenceFiles([...kept, ...incoming]);
}

/** Drop locker files the user deleted from Evidence Locker itself. */
export function forgetRetainedEvidenceFiles(files = []) {
  const drop = new Set((files || []).map((file) => String(file?.id || file || '')));
  const next = loadRetainedEvidenceFiles().filter((file) => !drop.has(String(file?.id || '')));
  return saveRetainedEvidenceFiles(next);
}

/**
 * Collect vault file entries from demand matters (attachments + evidence/document URLs).
 * Files retained after a demand is deleted are included when that demand is no longer in the list.
 * @returns {{ id: string, name: string, kind: string, source: string, matterId: string, debtorName: string, matterLabel: string, demandId: string, demandDeleted: boolean, bytes: number, url: string|null }[]}
 */
export function collectEvidenceVaultFiles(matters = []) {
  const files = filesFromMatters(matters);
  const seen = new Set(files.map((file) => file.id));
  const present = new Set(
    (matters || []).map((matter) => String(matter?.ROWID || '')).filter(Boolean)
  );

  for (const file of loadRetainedEvidenceFiles()) {
    const matterId = String(file?.matterId || '');
    const id = String(file?.id || '');
    if (!matterId || !id || present.has(matterId) || seen.has(id)) continue;
    seen.add(id);
    files.push({ ...file, demandDeleted: true });
  }

  return files.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Resolve a viewable URL for an Evidence Locker file (same rules as demand EvidenceUpload).
 * @returns {Promise<string|null>}
 */
export async function resolveEvidenceLockerFileUrl(file = {}) {
  const rawUrl = String(file?.url || '').trim();
  if (isOpenableAttachmentUrl(rawUrl)) return rawUrl;
  const fromViewer = evidenceViewerUrl(rawUrl);
  if (fromViewer) return fromViewer;
  const matterId = String(file?.matterId || '').trim();
  const name = String(file?.name || '').trim();
  if (!matterId || !name) return null;
  return resolveUploadBlobUrl('evidence', matterId, name);
}

/**
 * Remove one locker file from a matter record (attachments or vault URL fields).
 * @returns {object} patched matter
 */
export function removeEvidenceFileFromMatter(matter = {}, file = {}) {
  const nameKey = String(file?.name || '')
    .trim()
    .toLowerCase();
  if (!nameKey) return { ...matter };

  const source =
    file.source ||
    (String(file.id || '').includes('::att::')
      ? 'attachment'
      : String(file.id || '').includes('Document_Vault_URL')
        ? 'Document_Vault_URL'
        : 'Evidence_Vault_URL');

  const next = { ...matter, Updated_At: new Date().toISOString() };

  if (source === 'attachment') {
    next.Attachments = (matter.Attachments || []).filter((att) => {
      const name = attachmentName(att).toLowerCase();
      return name !== nameKey;
    });
    return next;
  }

  if (source === 'Document_Vault_URL') {
    const names = basenameFromUrl(matter.Document_Vault_URL).map((n) => n.toLowerCase());
    if (names.includes(nameKey) || !names.length) {
      next.Document_Vault_URL = null;
    }
    return next;
  }

  const remaining = basenameFromUrl(matter.Evidence_Vault_URL).filter(
    (name) => name.toLowerCase() !== nameKey
  );
  if (remaining.length) {
    next.Evidence_Vault_URL = `pending-upload://${remaining
      .map((name) => encodeURIComponent(name))
      .join(',')}`;
    next.evidenceFileNames = remaining;
  } else {
    next.Evidence_Vault_URL = null;
    next.evidenceFileNames = [];
  }
  return next;
}

export function sumEvidenceVaultBytes(files = []) {
  return (files || []).reduce((sum, file) => sum + (Number(file.bytes) || 0), 0);
}

export function selectedEvidenceFilesFromIds(files = [], selectedIds = new Set()) {
  const ids = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
  if (!ids.size) return [];
  return (files || []).filter((file) => ids.has(String(file?.id || '')));
}

/**
 * Apply multiple locker file removals, grouping by demand so each matter is patched once.
 * @returns {{ matters: object[], removedCount: number }}
 */
export function removeEvidenceFilesFromMatters(matters = [], files = []) {
  const byMatter = new Map();
  for (const file of files || []) {
    const matterId = String(file?.matterId || '').trim();
    if (!matterId) continue;
    if (!byMatter.has(matterId)) byMatter.set(matterId, []);
    byMatter.get(matterId).push(file);
  }

  const matterById = new Map(
    (matters || [])
      .filter((matter) => matter?.ROWID != null)
      .map((matter) => [String(matter.ROWID), matter])
  );

  const patched = [];
  let removedCount = 0;
  for (const [matterId, matterFiles] of byMatter) {
    let matter = matterById.get(matterId);
    if (!matter) continue;
    for (const file of matterFiles) {
      matter = removeEvidenceFileFromMatter(matter, file);
      removedCount += 1;
    }
    patched.push(matter);
  }
  return { matters: patched, removedCount };
}

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc ^= bytes[i];
    for (let j = 0; j < 8; j += 1) {
      const mask = -(crc & 1);
      crc = (crc >>> 1) ^ (0xedb88320 & mask);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function u16(value) {
  return [value & 0xff, (value >>> 8) & 0xff];
}

function u32(value) {
  return [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff];
}

function encodeZipPath(name) {
  const raw = String(name || 'file')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\.\.\//g, '');
  return raw || 'file';
}

/** Build an uncompressed (STORE) ZIP from named byte arrays. */
export function buildZipArchive(entries = []) {
  const locals = [];
  const centrals = [];
  let offset = 0;

  for (const entry of entries || []) {
    const name = encodeZipPath(entry?.name);
    const data =
      entry?.bytes instanceof Uint8Array
        ? entry.bytes
        : new Uint8Array(entry?.bytes || []);
    const nameBytes = new TextEncoder().encode(name);
    const crc = crc32(data);
    const size = data.length;
    const local = new Uint8Array([
      0x50,
      0x4b,
      0x03,
      0x04,
      ...u16(20),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(crc),
      ...u32(size),
      ...u32(size),
      ...u16(nameBytes.length),
      ...u16(0),
      ...nameBytes,
      ...data,
    ]);
    const central = new Uint8Array([
      0x50,
      0x4b,
      0x01,
      0x02,
      ...u16(20),
      ...u16(20),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(crc),
      ...u32(size),
      ...u32(size),
      ...u16(nameBytes.length),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(0),
      ...u32(offset),
      ...nameBytes,
    ]);
    locals.push(local);
    centrals.push(central);
    offset += local.length;
  }

  const centralSize = centrals.reduce((sum, part) => sum + part.length, 0);
  const end = new Uint8Array([
    0x50,
    0x4b,
    0x05,
    0x06,
    ...u16(0),
    ...u16(0),
    ...u16(centrals.length),
    ...u16(centrals.length),
    ...u32(centralSize),
    ...u32(offset),
    ...u16(0),
  ]);

  const total =
    locals.reduce((sum, part) => sum + part.length, 0) + centralSize + end.length;
  const out = new Uint8Array(total);
  let cursor = 0;
  for (const part of [...locals, ...centrals, end]) {
    out.set(part, cursor);
    cursor += part.length;
  }
  return out;
}

function uniqueZipEntryName(name, used) {
  const base = encodeZipPath(name);
  if (!used.has(base.toLowerCase())) {
    used.add(base.toLowerCase());
    return base;
  }
  const dot = base.lastIndexOf('.');
  const stem = dot > 0 ? base.slice(0, dot) : base;
  const ext = dot > 0 ? base.slice(dot) : '';
  let n = 2;
  let next = `${stem}-${n}${ext}`;
  while (used.has(next.toLowerCase())) {
    n += 1;
    next = `${stem}-${n}${ext}`;
  }
  used.add(next.toLowerCase());
  return next;
}

async function readUrlBytes(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}`);
  }
  return new Uint8Array(await response.arrayBuffer());
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }, 1500);
}

/**
 * Download selected locker files. One file downloads directly; multiple become a ZIP.
 * @returns {Promise<{ downloaded: number, skipped: number, zip: boolean }>}
 */
export async function downloadEvidenceLockerFiles(
  files = [],
  { urlById = {}, filename } = {}
) {
  const rows = files || [];
  if (!rows.length) return { downloaded: 0, skipped: 0, zip: false };

  const payloads = [];
  let skipped = 0;
  const usedNames = new Set();

  for (const file of rows) {
    const url =
      urlById[file.id] ||
      (await resolveEvidenceLockerFileUrl(file).catch(() => null));
    if (!url) {
      skipped += 1;
      continue;
    }
    try {
      const bytes = await readUrlBytes(url);
      payloads.push({
        name: uniqueZipEntryName(file.name || 'file', usedNames),
        bytes,
      });
    } catch {
      skipped += 1;
    }
  }

  if (!payloads.length) return { downloaded: 0, skipped, zip: false };

  const stamp = new Date().toISOString().slice(0, 10);
  if (payloads.length === 1) {
    triggerBlobDownload(
      new Blob([payloads[0].bytes]),
      payloads[0].name || `demandjet-evidence-${stamp}`
    );
    return { downloaded: 1, skipped, zip: false };
  }

  const zipBytes = buildZipArchive(payloads);
  triggerBlobDownload(
    new Blob([zipBytes], { type: 'application/zip' }),
    filename || `demandjet-evidence-${stamp}.zip`
  );
  return { downloaded: payloads.length, skipped, zip: true };
}

export function formatStorageBytes(bytes) {
  const value = Math.max(0, Number(bytes) || 0);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < BYTES_PER_GB) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / BYTES_PER_GB).toFixed(2)} GB`;
}

export function buildEvidenceVaultUsage({ usedBytes = 0, billing = {} } = {}) {
  const entitlement = getEvidenceVaultEntitlement(billing);
  const used = Math.max(0, Number(usedBytes) || 0);
  const remaining = Math.max(0, entitlement.limitBytes - used);
  const ratio = entitlement.limitBytes > 0 ? used / entitlement.limitBytes : 0;
  const atWarn = ratio >= EVIDENCE_VAULT_WARN_RATIO && ratio < 1;
  const atLimit = used >= entitlement.limitBytes;
  const overLimit = used > entitlement.limitBytes;
  const upgradeOptions = getEvidenceVaultTiersAbove(entitlement.planId);
  const nextTier = upgradeOptions[0] || null;
  const usedGb = used / BYTES_PER_GB;

  return {
    ...entitlement,
    usedBytes: used,
    remainingBytes: remaining,
    usedGb,
    remainingGb: remaining / BYTES_PER_GB,
    ratio,
    atWarn,
    atLimit,
    overLimit,
    nextTier,
    upgradeOptions,
    label: `${formatStorageBytes(used)} / ${entitlement.gbLimit} GB · ${entitlement.tierLabel}`,
  };
}

/**
 * Whether uploading `addBytes` is allowed. Hard-blocks at 100% capacity.
 */
export function canUploadEvidence({ usedBytes = 0, addBytes = 0, billing = {} } = {}) {
  const usage = buildEvidenceVaultUsage({ usedBytes, billing });
  const add = Math.max(0, Number(addBytes) || 0);
  if (add <= usage.remainingBytes) {
    return { ok: true, usage, remainingBytes: usage.remainingBytes - add };
  }
  return {
    ok: false,
    usage,
    remainingBytes: usage.remainingBytes,
    shortfallBytes: add - usage.remainingBytes,
  };
}

export function evidenceVaultLimitMessage(usage) {
  const next = usage?.nextTier;
  if (next) {
    return (
      `Evidence Locker is full (${formatStorageBytes(usage.usedBytes)} / ${usage.gbLimit} GB on ${usage.tierLabel}). ` +
      `Delete files from Evidence Locker or upgrade to ${next.label} (${next.gbLimit} GB · $${next.monthlyPrice}/mo) to upload more.`
    );
  }
  return (
    `Evidence Locker is full (${formatStorageBytes(usage.usedBytes)} / ${usage.gbLimit} GB on ${usage.tierLabel}). ` +
    'Delete files from Evidence Locker or contact support for more capacity.'
  );
}

export function evidenceVaultWarnMessage(usage) {
  return (
    `Evidence Locker is ${Math.round((usage?.ratio || 0) * 100)}% full ` +
    `(${formatStorageBytes(usage?.usedBytes)} / ${usage?.gbLimit} GB). ` +
    'Delete files you no longer need, or upgrade storage before you hit the limit.'
  );
}

export function setEvidenceVaultPlan(billing = {}, planId = 'included', extras = {}) {
  const tier = getEvidenceVaultTiersById(planId);
  const now = new Date().toISOString();
  const paid = tier.id !== 'included';
  return {
    ...billing,
    evidenceVaultPlanId: tier.id,
    evidenceVaultEnabled: paid,
    evidenceVaultEnabledAt: paid
      ? extras.enabledAt || billing.evidenceVaultEnabledAt || now
      : null,
    evidenceVaultStripePriceId: tier.stripePriceId,
    evidenceVaultStripeItemId: paid
      ? extras.stripeItemId || billing.evidenceVaultStripeItemId || null
      : null,
    evidenceVaultStripeSubscriptionId: paid
      ? extras.stripeSubscriptionId || billing.evidenceVaultStripeSubscriptionId || null
      : null,
  };
}
